import { useState } from 'react'
import { Image, Video, File, Download, ExternalLink, Music } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { getMediaUrl } from '@/services/api'
import type { Message } from '@/types/message'

interface MessageMediaProps {
  mediaFile: NonNullable<Message['mediaFile']>
  isOwnMessage?: boolean
  className?: string
}

export function MessageMedia({ mediaFile, isOwnMessage = false, className }: MessageMediaProps) {
  const [imageError, setImageError] = useState(false)
  const [showFullImage, setShowFullImage] = useState(false)

  const getMediaType = (mimeType: string): 'image' | 'video' | 'document' | 'audio' => {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    return 'document'
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const mediaType = getMediaType(mediaFile.type)

  // URL completa usando função centralizada. Download/abrir usam a MESMA
  // fullUrl: antes usavam mediaFile.url cru, que para URLs relativas do
  // backend apontava para o host errado.
  const fullUrl = getMediaUrl(mediaFile.url)

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = fullUrl
    link.download = mediaFile.filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handleOpenInNewTab = () => {
    window.open(fullUrl, '_blank')
  }

  return (
    <div className={cn("relative", className)}>
      {mediaType === 'image' && (
        <div className="relative group">
          {!imageError ? (
            <img
              src={fullUrl}
              alt={mediaFile.filename}
              className={cn(
                "max-w-full rounded-lg cursor-pointer transition-opacity",
                "max-h-[300px] object-contain",
                isOwnMessage ? "ml-auto" : "mr-auto"
              )}
              onClick={() => setShowFullImage(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex items-center justify-center p-8 bg-game-primary rounded-lg border border-game-primary">
              <div className="text-center">
                <Image className="h-12 w-12 mx-auto mb-2 text-game-secondary" />
                <p className="text-sm text-game-secondary">Erro ao carregar imagem</p>
              </div>
            </div>
          )}
          
          {/* Overlay com informações */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/60 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-white truncate">{mediaFile.filename}</p>
            <p className="text-xs text-gray-300">{formatFileSize(mediaFile.size)}</p>
          </div>
        </div>
      )}

      {mediaType === 'video' && (
        <div className="relative group">
          <video
            src={fullUrl}
            controls
            className={cn(
              "max-w-full rounded-lg",
              "max-h-[400px]",
              isOwnMessage ? "ml-auto" : "mr-auto"
            )}
          />
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/60 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-xs text-white truncate">{mediaFile.filename}</p>
            <p className="text-xs text-gray-300">{formatFileSize(mediaFile.size)}</p>
          </div>
        </div>
      )}

      {mediaType === 'audio' && (
        <div className={cn(
          "flex items-center gap-3 p-4 bg-game-primary rounded-lg border border-game-primary",
          isOwnMessage ? "ml-auto max-w-md" : "mr-auto max-w-md"
        )}>
          <Music className="h-8 w-8 text-game-accent flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-game-primary truncate">{mediaFile.filename}</p>
            <p className="text-xs text-game-secondary">{formatFileSize(mediaFile.size)}</p>
          </div>
          <audio src={fullUrl} controls className="flex-1" />
        </div>
      )}

      {mediaType === 'document' && (
        <div className={cn(
          "flex items-center gap-3 p-4 bg-game-primary rounded-lg border border-game-primary hover:bg-game-hover transition-colors",
          isOwnMessage ? "ml-auto max-w-md" : "mr-auto max-w-md"
        )}>
          <File className="h-8 w-8 text-game-accent flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-game-primary truncate">{mediaFile.filename}</p>
            <p className="text-xs text-game-secondary">{formatFileSize(mediaFile.size)}</p>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenInNewTab}
              className="h-8 w-8 p-0"
              title="Abrir em nova aba"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal de imagem em tela cheia */}
      {showFullImage && mediaType === 'image' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={fullUrl}
              alt={mediaFile.filename}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white"
            >
              ✕
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
